import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { ActionMessage, SnackType } from '@models/common.model';
import { clearActionMessage } from '@redux/reducers/actionMessages.reducer';
import { useAppDispatch, useAppSelector } from '@redux/hooks';

/**
 * Sprint 49, story-243 (j0ue5Mow). The Redux action-message queue (`appendActionMessage`, 51 call
 * sites) had no consumer, so every message pushed through it was silently dropped. This atom has
 * no UI of its own: it drains the queue into react-toastify (the app's single toast surface) and
 * clears each message once shown. Mounted beside `ThemedToasts` in `src/index.tsx`.
 */
const showToast = (message: ActionMessage): void => {
  const options = { toastId: message.id };
  switch (message.type) {
    case SnackType.SUCCESS:
      toast.success(message.message, options);
      break;
    case SnackType.ERROR:
      toast.error(message.message, options);
      break;
    case SnackType.WARNING:
      toast.warning(message.message, options);
      break;
    default:
      toast.info(message.message, options);
  }
};

const ActionMessageBridge = () => {
  const dispatch = useAppDispatch();
  const actionMessages = useAppSelector(
    (state) => state.actionMessages?.actionMessages,
  );
  // Ids already toasted: guards a re-render or double effect run from showing a message twice
  // before its clear action has been applied.
  const shown = useRef<Set<string>>(new Set());

  useEffect(() => {
    (actionMessages ?? []).forEach((message) => {
      const id = message.id;
      if (!id || shown.current.has(id)) {
        return;
      }
      shown.current.add(id);
      if (message.message) {
        showToast(message);
      }
      dispatch(clearActionMessage(message));
    });
  }, [actionMessages, dispatch]);

  return null;
};

export default ActionMessageBridge;
